/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// chartImage.js — builds the chart that goes on the export's 2nd worksheet as a
// PNG image. Depletions = a line / stacked-bar timeline by the chosen Group-By;
// Inventory = a snapshot category bar chart. SVG is rendered to a PNG via canvas
// (same trick as the logo).

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_ABBR = MONTHS.map((m) => m.slice(0, 3));

// Leads with the app orange #F2A33C; the retired #E85D26 is gone (backlog 166).
// ⚠️ Must stay byte-identical to the same array in
// `sales-forecasting/utils/exportForecast.js` and `ForecastChart.jsx` — a
// downloaded chart should be the same colours as the app that produced it.
const CHART_COLORS = [
  "#F2A33C", "#3b82f6", "#4eca8d", "#a78bfa", "#22d3ee",
  "#84cc16", "#f472b6", "#facc15", "#60a5fa", "#34d399",
  "#f87171", "#c084fc", "#fbbf24", "#fb923c", "#2dd4bf",
];

// LINE MODE ONLY — backlog 323. `buildTimelineChartSvg` draws a 2.5px polyline
// on the #FFFFFF export sheet, and a stroke that thin is a graphical object
// WCAG 1.4.11 wants at 3:1. CHART_COLORS on white measures 1.53:1 (#facc15) to
// 3.68:1 (#3b82f6) — 14 of the 15 fail, the lead orange at 2.08:1. Same hues +
// saturations, lightness squeezed to clear 3:1 on white (3.19–4.11:1) and on
// the app's dark canvas #1B2A4A (3.46–4.45:1). Bars and the inventory category
// chart keep CHART_COLORS — large fills, already fine.
// ⚠️ Must stay byte-identical to LINE_COLORS in
// `sales-forecasting/utils/exportForecast.js` and `ForecastChart.jsx`, for the
// same reason CHART_COLORS must (backlog 166).
const LINE_COLORS = [
  "#C5750D", "#2C78F5", "#2E9A65", "#8D69F9", "#0D99AF",
  "#629810", "#EF3194", "#AF8C04", "#2282F8", "#229C70",
  "#F53636", "#AA59FB", "#B78503", "#D96404", "#209B8C",
];

function esc(v) {
  return String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}
function niceNum(x) {
  if (x <= 0) return 1;
  const exp = Math.floor(Math.log10(x));
  const f = x / Math.pow(10, exp);
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nf * Math.pow(10, exp);
}
const kfmt = (v) => (v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : String(Math.round(v)));

// Wrap a long label onto at most 2 lines that each fit `maxChars` (word-aware,
// ellipsis on the 2nd line if it still overflows).
function splitTwoLines(text, maxChars) {
  if (text.length <= maxChars) return [text];
  const words = text.split(" ");
  if (words.length === 1) {
    const head = text.slice(0, maxChars);
    let tail = text.slice(maxChars);
    if (tail.length > maxChars) tail = tail.slice(0, Math.max(1, maxChars - 1)) + "…";
    return [head, tail];
  }
  let l1 = "", i = 0;
  for (; i < words.length; i++) {
    const next = l1 ? `${l1} ${words[i]}` : words[i];
    if (next.length > maxChars && l1) break;
    l1 = next;
  }
  let l2 = words.slice(i).join(" ");
  if (l2.length > maxChars) l2 = l2.slice(0, Math.max(1, maxChars - 1)) + "…";
  return [l1, l2];
}

// ── Rasterize an SVG string to a PNG (base64, no prefix) ─────────────────────
export function svgToPng(svg, width, height) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve({ base64: canvas.toDataURL("image/png").split(",")[1], width, height });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svg)));
  });
}

// ── Depletions: aggregate filtered rows into { labels, series } ──────────────
function seriesLabelFor(r, groupBy) {
  switch (groupBy) {
    case "region":      return r.states?.region || "Unknown";
    case "state":       return r.states?.state_name || "Unknown";
    case "distributor": return r.states?.distributor || "Unknown";
    case "flavor":      return r.flavors?.name || "Unknown";
    case "size":        return r.sizes?.name || "Unknown";
    case "pkg":         return r.pack_sizes?.name || "Unknown";
    default: {
      const f = r.flavors?.name, s = r.sizes?.name;
      return f && s ? `${f} ${s}` : (f || s || "Unknown");
    }
  }
}

export function depletionSeries(rows, groupBy) {
  const gb = groupBy || "flavor_size";
  const colKey = (y, m) => `${y}-${String(m).padStart(2, "0")}`;
  const seriesSet = new Set();
  const colSet = new Set();
  rows.forEach((r) => { seriesSet.add(seriesLabelFor(r, gb)); colSet.add(colKey(r.year, r.month)); });
  const names = [...seriesSet].sort();
  const cols = [...colSet].sort();
  const multiYear = new Set(rows.map((r) => r.year)).size > 1;

  const acc = {};
  rows.forEach((r) => {
    const ck = colKey(r.year, r.month), key = seriesLabelFor(r, gb);
    (acc[ck] ??= {})[key] = (acc[ck]?.[key] ?? 0) + (Number(r.case_sales) || 0);
  });

  const labels = cols.map((ck) => {
    const [y, m] = ck.split("-");
    return multiYear ? `${MONTH_ABBR[Number(m) - 1]} ${String(y).slice(2)}` : MONTH_ABBR[Number(m) - 1];
  });
  const series = names
    .map((name) => ({ name, values: cols.map((ck) => acc[ck]?.[name] ?? 0) }))
    .filter((s) => s.values.some((v) => v > 0))
    .sort((a, b) => b.values.reduce((x, y) => x + y, 0) - a.values.reduce((x, y) => x + y, 0));
  return { labels, series };
}

// ── Depletions chart: line / stacked-bar timeline ────────────────────────────
export function buildTimelineChartSvg({ labels, series, title, type = "line", axisLabel = "CASES" }, width = 920, height = 470) {
  const left = 70, right = 220, top = 48, bottom = 52;
  const plotW = width - left - right, plotH = height - top - bottom;
  const n = labels.length;
  const isBar = type === "bar";
  // backlog 323 — thin strokes need the 3:1 palette, large fills do not.
  const palette = isBar ? CHART_COLORS : LINE_COLORS;
  const yB = top + plotH;

  const rawMax = isBar
    ? labels.reduce((mx, _, i) => Math.max(mx, series.reduce((s, ser) => s + (ser.values[i] || 0), 0)), 0)
    : series.reduce((mx, s) => Math.max(mx, ...s.values), 0);
  const step = niceNum((rawMax || 1) / 5);
  const niceMax = Math.max(step * 5, Math.ceil((rawMax || 1) / step) * step);
  const yAt = (v) => yB - (v / niceMax) * plotH;
  // Bars sit in evenly divided bands (inset from the axis); lines/points span
  // edge-to-edge. Keeping these separate is what stops bar 1 covering the Y-axis.
  const slotW = n > 0 ? plotW / n : plotW;
  const bandX = (i) => left + slotW * (i + 0.5);
  const lineX = (i) => (n <= 1 ? left + plotW / 2 : left + (i * plotW) / (n - 1));
  const xAt = isBar ? bandX : lineX;

  let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`;
  svg += `<rect x="0" y="0" width="${width}" height="${height}" fill="#FFFFFF"/>`;
  if (title) svg += `<text x="${(left + plotW / 2).toFixed(1)}" y="28" text-anchor="middle" font-family="Inter,Segoe UI,sans-serif" font-size="16" font-weight="800" fill="#1B2A4A">${esc(title)}</text>`;
  for (let t = 0; t <= 5; t++) {
    const v = (niceMax / 5) * t, y = yAt(v);
    svg += `<line x1="${left}" y1="${y.toFixed(1)}" x2="${left + plotW}" y2="${y.toFixed(1)}" stroke="#E2E8F0" stroke-width="1"/>`;
    svg += `<text x="${left - 10}" y="${(y + 4).toFixed(1)}" text-anchor="end" font-family="Inter,Segoe UI,sans-serif" font-size="11" fill="#6B7A99">${esc(kfmt(v))}</text>`;
  }
  svg += `<text transform="translate(18 ${top + plotH / 2}) rotate(-90)" text-anchor="middle" font-family="Inter,Segoe UI,sans-serif" font-size="11" font-weight="600" fill="#6B7A99" letter-spacing="0.05em">${esc(axisLabel)}</text>`;
  const showEvery = n > 14 ? 2 : 1;
  labels.forEach((lab, i) => {
    if (i % showEvery !== 0 && i !== n - 1) return;
    svg += `<text x="${xAt(i).toFixed(1)}" y="${yB + 18}" text-anchor="middle" font-family="Inter,Segoe UI,sans-serif" font-size="10" fill="#6B7A99">${esc(lab)}</text>`;
  });
  svg += `<line x1="${left}" y1="${yB}" x2="${left + plotW}" y2="${yB}" stroke="#CBD5E1" stroke-width="1"/>`;

  if (isBar) {
    const bw = Math.max(6, Math.min(34, slotW * 0.62));
    labels.forEach((_, i) => {
      let acc = 0;
      series.forEach((s, si) => {
        const v = s.values[i] || 0;
        if (v <= 0) return;
        const h = (v / niceMax) * plotH, yTop = yB - ((acc + v) / niceMax) * plotH;
        svg += `<rect x="${(bandX(i) - bw / 2).toFixed(1)}" y="${yTop.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" fill="${palette[si % palette.length]}"/>`;
        acc += v;
      });
    });
  } else {
    series.forEach((s, si) => {
      const color = palette[si % palette.length];
      const pts = s.values.map((v, i) => `${lineX(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(" ");
      svg += `<polyline points="${pts}" fill="none" stroke="${color}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>`;
      s.values.forEach((v, i) => { svg += `<circle cx="${lineX(i).toFixed(1)}" cy="${yAt(v).toFixed(1)}" r="2.6" fill="${color}"/>`; });
    });
  }

  const lx = left + plotW + 18;
  let ly = top + 2;
  const maxRows = Math.floor(plotH / 18);
  series.slice(0, maxRows).forEach((s, si) => {
    const name = s.name.length > 26 ? s.name.slice(0, 25) + "…" : s.name;
    // Swatch takes the mark's own colour — a chip in the fill orange beside a
    // line in the 3:1 orange just relocates the failure into the legend (323).
    svg += `<rect x="${lx}" y="${ly - 8}" width="12" height="12" rx="2" fill="${palette[si % palette.length]}"/>`;
    svg += `<text x="${lx + 18}" y="${ly + 2}" font-family="Inter,Segoe UI,sans-serif" font-size="11" fill="#374151">${esc(name)}</text>`;
    ly += 18;
  });
  if (series.length > maxRows) svg += `<text x="${lx}" y="${ly + 2}" font-family="Inter,Segoe UI,sans-serif" font-size="11" font-style="italic" fill="#6B7A99">+${series.length - maxRows} more…</text>`;
  svg += `</svg>`;
  return svg;
}

// ── Inventory chart: one bar per category (snapshot) ─────────────────────────
export function buildCategoryBarSvg({ categories, values, title, axisLabel = "UNITS" }, width = 920, height = 470) {
  const n = categories.length;
  // Reserve more height for the angled category labels the more of them there
  // are (and the longer they are), so they never clip the bottom of the image.
  const longest = categories.reduce((m, c) => Math.max(m, c.length), 0);
  const maxChars = n > 14 ? 14 : 18;            // wrap threshold per line
  const wraps = categories.some((c) => c.length > maxChars);
  const bottom = Math.min(168, 70 + Math.min(longest, maxChars * 2) * 3.2 + (wraps ? 16 : 0));
  const left = 70, right = 26, top = 48;
  const plotW = width - left - right, plotH = height - top - bottom;
  const rawMax = Math.max(0, ...values);
  const step = niceNum((rawMax || 1) / 5);
  const niceMax = Math.max(step * 5, Math.ceil((rawMax || 1) / step) * step);
  const yB = top + plotH;
  const slotW = n > 0 ? plotW / n : plotW;
  const bw = Math.max(6, Math.min(48, slotW * 0.62));
  const cAt = (i) => left + slotW * (i + 0.5);

  let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`;
  svg += `<rect x="0" y="0" width="${width}" height="${height}" fill="#FFFFFF"/>`;
  if (title) svg += `<text x="${(left + plotW / 2).toFixed(1)}" y="28" text-anchor="middle" font-family="Inter,Segoe UI,sans-serif" font-size="16" font-weight="800" fill="#1B2A4A">${esc(title)}</text>`;
  for (let t = 0; t <= 5; t++) {
    const v = (niceMax / 5) * t, y = yB - (v / niceMax) * plotH;
    svg += `<line x1="${left}" y1="${y.toFixed(1)}" x2="${left + plotW}" y2="${y.toFixed(1)}" stroke="#E2E8F0" stroke-width="1"/>`;
    svg += `<text x="${left - 10}" y="${(y + 4).toFixed(1)}" text-anchor="end" font-family="Inter,Segoe UI,sans-serif" font-size="11" fill="#6B7A99">${esc(kfmt(v))}</text>`;
  }
  svg += `<text transform="translate(18 ${top + plotH / 2}) rotate(-90)" text-anchor="middle" font-family="Inter,Segoe UI,sans-serif" font-size="11" font-weight="600" fill="#6B7A99" letter-spacing="0.05em">${esc(axisLabel)}</text>`;

  categories.forEach((cat, i) => {
    const v = values[i] || 0;
    const h = (v / niceMax) * plotH, x = cAt(i) - bw / 2, y = yB - h;
    svg += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" rx="2" fill="${CHART_COLORS[i % CHART_COLORS.length]}"/>`;
    if (v > 0) svg += `<text x="${cAt(i).toFixed(1)}" y="${(y - 5).toFixed(1)}" text-anchor="middle" font-family="Inter,Segoe UI,sans-serif" font-size="10" font-weight="600" fill="#6B7A99">${esc(kfmt(v))}</text>`;
    const lines = splitTwoLines(cat, maxChars);
    const tspans = lines.map((ln, li) => `<tspan x="0" dy="${li === 0 ? 0 : 11}">${esc(ln)}</tspan>`).join("");
    svg += `<text transform="translate(${cAt(i).toFixed(1)} ${yB + 12}) rotate(-32)" text-anchor="end" font-family="Inter,Segoe UI,sans-serif" font-size="10" fill="#6B7A99">${tspans}</text>`;
  });
  svg += `<line x1="${left}" y1="${yB}" x2="${left + plotW}" y2="${yB}" stroke="#CBD5E1" stroke-width="1"/>`;
  svg += `</svg>`;
  return svg;
}
